import { CanalValues } from './../users/usersSlice';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from 'src/api/client';
import { RootState } from 'src/store';
import { RequestStatus } from '../auth/authSlice';

export enum ProspectStatus {
  PROSPECT = 'PROSPECT',
  PROSPECT_FINALISE = 'PROSPECT_FINALISE',
  CLIENT_CONVERTI = 'CLIENT_CONVERTI',
  CLIENT_STATIONNAIRE = 'CLIENT_STATIONNAIRE',
  CLIENT_ENROLE = 'CLIENT_ENROLE',
  DEMANDE_OFF = 'DEMANDE_OFF',
  PROSPECT_DISABLED_C = 'PROSPECT_DISABLED_C',
  PROSPECT_DISABLED_TC = 'PROSPECT_DISABLED_TC',
  CLIENT_DISABLED_C = 'CLIENT_DISABLED_C',
  CLIENT_DISABLED_TC = 'CLIENT_DISABLED_TC'
}

type ProcessingCode =
  | 'A_RELANCER'
  | 'A_TRAITER'
  | 'PARCOURS_A_FINALISER'
  | 'INJOIGNABLE_1'
  | 'INJOIGNABLE_2'
  | 'INJOIGNABLE_3'
  | 'DOSSIER_COMPLET'
  | 'DOSSIER_INCOMPLET'
  | 'TRANSFERT_EN_ATTENTE'
  | 'TRANSFERT_NON_CONFORME'
  | 'TRANSFERT_OK'
  | 'DEMANDE_OFF';

export interface MotifItem {
  code: string;
  dateInserted: string;
  uuid: string;
  value: string;
}

export type ProcessingStatus = {
  code: ProcessingCode;
  dateInserted: string;
  disabled: boolean;
  uuid: string;
  value: string;
  client: boolean;
  prospect: boolean;
  motifs: MotifItem[];
};

export type DocTypes = {
  code: string;
  dateInserted: string;
  label: string;
  label_fr: string;
  uuid: string;
  recto: boolean;
  verso: boolean;
};
export type TypeObjectBack = {
  code: string;
  uuid: string;
  label: string;
};

export interface CountriesObjectType extends TypeObjectBack {
  indicatif: string;
  documentTypes: DocTypes[];
}

interface UserStatus {
  uuid: string;
  code: ProspectStatus;
  label: string;
  client: boolean;
  etatTraitements: ProcessingStatus[];
}

export type TypeCanal = keyof typeof CanalValues;

export interface UserCanal {
  code: TypeCanal;
  label: string;
}

interface InitialState {
  status: RequestStatus;
  etatTraitements: ProcessingStatus[];
  countries: CountriesObjectType[];
  activityAreas: TypeObjectBack[];
  fundsSources: TypeObjectBack[];
  frequencies: TypeObjectBack[];
  nationalities: TypeObjectBack[];
  periodicities: TypeObjectBack[];
  professions: TypeObjectBack[];
  relationsShip: TypeObjectBack[];
  tranches: TypeObjectBack[];
  transfertMotifs: TypeObjectBack[];
  banks: TypeObjectBack[];
  userStatus: UserStatus[];
  docTypes: DocTypes[];
  userCanal: UserCanal[];
}
const initialState: InitialState = {
  status: 'idle',
  etatTraitements: [],
  countries: [],
  activityAreas: [],
  fundsSources: [],
  frequencies: [],
  nationalities: [],
  periodicities: [],
  professions: [],
  relationsShip: [],
  tranches: [],
  transfertMotifs: [],
  banks: [],
  userStatus: [],
  docTypes: [],
  userCanal: []
};

interface GeneralParamsResponse {
  etatTraitements: ProcessingStatus[];
  countries: CountriesObjectType[];
  activityAreas: TypeObjectBack[];
  fundsSources: TypeObjectBack[];
  frequencies: TypeObjectBack[];
  nationalities: TypeObjectBack[];
  periodicities: TypeObjectBack[];
  professions: TypeObjectBack[];
  relationsShip: TypeObjectBack[];
  tranches: TypeObjectBack[];
  transfertMotifs: TypeObjectBack[];
  banks: TypeObjectBack[];
  userStatus: UserStatus[];
  docTypes: DocTypes[];
  userCanal: UserCanal[];
}

export const fetchGeneraleParams = createAsyncThunk<GeneralParamsResponse>(
  'app/fetch',
  async () => {
    const { data } = await apiGet('/parameters/general');
    return data;
  }
);

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchGeneraleParams.pending, (state) => {
      state.status = 'loading';
    });

    builder.addCase(fetchGeneraleParams.fulfilled, (state, action) => {
      state.status = 'success';
      state.etatTraitements = action.payload.etatTraitements;
      state.countries = action.payload.countries;
      state.activityAreas = action.payload.activityAreas;
      state.fundsSources = action.payload.fundsSources;
      state.frequencies = action.payload.frequencies;
      state.nationalities = action.payload.nationalities;
      state.periodicities = action.payload.periodicities;
      state.professions = action.payload.professions;
      state.relationsShip = action.payload.relationsShip;
      state.tranches = action.payload.tranches;
      state.transfertMotifs = action.payload.transfertMotifs;
      state.banks = action.payload.banks;
      state.userStatus = action.payload.userStatus;
      state.docTypes = action.payload.docTypes;
      state.userCanal = action.payload.userCanal;
    });

    builder.addCase(fetchGeneraleParams.rejected, (state) => {
      state.status = 'error';
    });
  }
});

// select all params
export const selectGeneralParams = (state: RootState) => state.app;

// select available status
export const selectAvailableStatus = (state: RootState) => state.app.userStatus;

// select status by code
export const selectStatusByCode = (code: string) => (state: RootState) =>
  state.app.userStatus?.find((el) => el.code === code);

// select status by profile
export const selectStatusByProfile = (client: boolean) => (state: RootState) =>
  state.app.userStatus?.filter((el) => el.client === client);

// select user doctypes by county code
export const selectDocTypesByCountryCode = (countryCode: string) => (state: RootState) =>
  state.app.countries.find((country) => country.code === countryCode).documentTypes;

// select available processing status by status code
export const selectAvailableProcessByStatus = (status: string) => (state: RootState) =>
  state.app.userStatus?.find((el) => el.code === status)?.etatTraitements ?? [];

// select available processing status by profile
export const selectAvailableProcessByProfile = (client: boolean) => (state: RootState) =>
  state.app.etatTraitements?.filter((el) => (client ? el.client === client : el.prospect === true));

export const selectAvailableCanalTypes = (state: RootState) => state.app.userCanal;

export default appSlice.reducer;
